// hooks/useWorkflow.ts
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ESttausCode } from "../../../../enums";
import { showError, showSuccess } from "../../../../helpers/toast";
import { addOrderAsync, updateStatusOrderAsync } from "../../../../store/reducers/orderSlice";
import type { AppDispatch, RootState } from "../../../../store/store";
import { fetchWorkflowByProvider } from "../api/workflow.api";
import { mockFlowTasking } from "../MockData";
import { resolveTemplate } from "../services/workflow.service";

export const useWorkflow = (
   provider: string | null,
   setLoading?: (loading: boolean) => void,
   toggleConfirm?: () => void,
   selectedSatellite?: any
) =>
{
   const [workflow, setWorkflow] = useState<any>(null);
   const [currentStepIndex, setCurrentStepIndex] = useState(0);
   const [formData, setFormData] = useState<Record<string, any>>({});
   const [prevProvider, setPrevProvider] = useState<string | null>(null);

   const currentStep = workflow?.steps?.[currentStepIndex];

   const [fieldMeta, setFieldMeta] = useState<Record<string, any>>({});
   const [stepFieldMap, setStepFieldMap] = useState<Record<string, string[]>>(
      {},
   );

   const { t } = useTranslation();
   const navigate = useNavigate();
   const dispatch = useDispatch<AppDispatch>();
   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   useEffect(() =>
   {
      // Reset any previously loaded workflow immediately on provider change so a
      // provider without a supported schema (e.g. BlackSky) can't inherit the
      // previous provider's workflow — neither while the fetch is in-flight nor
      // when the response comes back carrying no workflow.
      setWorkflow(null);
      setCurrentStepIndex(0);
      setFormData({});
      setStepFieldMap({});
      setFieldMeta({});

      if (!provider) return;

      let cancelled = false;

      fetchWorkflowByProvider(provider, dispatch).then(({ data, error }) =>
      {
         if (cancelled) return;

         if (error)
         {
            setWorkflow(null);
            showError(error);

            return;
         }

         // Provider responded but has no usable workflow schema → keep workflow
         // null so the Next button stays disabled with the
         // "Unsupported provider's schema" tooltip.
         setWorkflow(data?.workflow ?? null);
      });

      return () =>
      {
         cancelled = true;
      };
   }, [provider, dispatch]);

   // fetch dynamic schema or use embedded schema
   useEffect(() =>
   {
      if (!currentStep || !workflow?.id) return;



      const fetchData = async () =>
      {
         let schema: any = null;
         let ui: any = null;
         try
         {
            if (currentStep.fetch_schema)
            {
               // Use mock data for development (simulate API response)
               const data = mockFlowTasking;
               // const data = await fetchStepSchema(currentStep.fetch_schema.url); // real API call
               schema = data.schema || data;
               ui = data.ui || generateUI(schema);
            } else if (currentStep.schema)
            {
               // Use embedded schema
               schema = currentStep.schema;
               ui = currentStep.ui || generateUI(schema);
            } else
            {
               return; // No schema to process
            }

            // Use step_id as the primary identifier, fallback to id
            const stepId = currentStep.step_id ?? currentStep.id;
            const uiOrder = ui?.order || [];
            const properties = schema.properties || {};

            // Store the field order for this step (use ui.order as source of truth)
            setStepFieldMap((prev) =>
            {
               const newMap = { ...prev };
               if (!newMap[stepId])
               {
                  newMap[stepId] = uiOrder;
               }
               return newMap;
            });

            setWorkflow((prev: any) =>
            {
               const steps = [...prev.steps];
               steps[currentStepIndex] = {
                  ...steps[currentStepIndex],
                  schema: schema,
                  ui: ui,
               };

               return { ...prev, steps };
            });

            // Build fieldMeta by merging schema.properties and ui.options
            // UI options take precedence for overlapping keys
            setFieldMeta((prev) =>
            {
               const newMeta = { ...prev };
               uiOrder.forEach((key: string) =>
               {
                  if (newMeta[key]) return; // Skip if already defined

                  const schemaField = properties[key];
                  const uiField = ui?.options?.[key];

                  // Start with schema properties as base
                  let merged: any = { ...schemaField };

                  // Merge UI options first (UI overrides schema for explicit properties)
                  if (uiField)
                  {
                     merged = { ...merged, ...uiField };
                  }

                  // Infer missing attributes from uiField.widget
                  if (uiField?.widget)
                  {
                     // Infer format if not set
                     if (
                        !merged.format &&
                        (uiField.widget === "date" || uiField.widget === "date-picker")
                     )
                     {
                        merged.format = "date-time";
                     }

                     // Infer type if not set
                     if (!merged.type)
                     {
                        if (["number", "range", "slider"].includes(uiField.widget))
                        {
                           merged.type = "number";
                        } else
                        {
                           merged.type = "string";
                        }
                     }

                     // Use uiField.options as enum if not set
                     if (uiField.options && !merged.enum)
                     {
                        merged.enum = uiField.options;
                     }
                  }

                  newMeta[key] = merged;
               });
               return newMeta;
            });

            // Initialize formData with defaults: prefer ui.options[key].default, fallback to schema.properties[key].default
            setFormData((prev) =>
            {
               const newData = { ...prev };
               uiOrder.forEach((key: string) =>
               {
                  if (newData[key] !== undefined) return;

                  const uiField = ui?.options?.[key];
                  const schemaField = properties[key];

                  if (uiField?.default !== undefined)
                  {
                     if (uiField?.mode === "multiple")
                     {
                        newData[key] = Array.isArray(uiField.default)
                           ? uiField.default
                           : [uiField.default];
                     } else
                     {
                        newData[key] = uiField.default;
                     }
                  } else if (schemaField?.default !== undefined)
                  {
                     newData[key] = schemaField.default;
                  }
               });
               return newData;
            });
         } catch (err)
         {
            console.error("Fetch schema error", err);
         }
      };

      fetchData();
   }, [currentStep?.step_id, currentStepIndex, workflow?.id]);

   const next = () =>
   {
      const nextId = currentStep?.next;
      const index = workflow.steps.findIndex((s: any) => s.step_id === nextId);
      if (index !== -1) setCurrentStepIndex(index);
   };

   const back = () =>
   {
      setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
   };

   const generateUI = (schema: any) =>
   {
      return {
         order: Object.keys(schema.properties || {}),
         options: Object.keys(schema.properties || {}).reduce((acc: any, key) =>
         {
            const field = schema.properties[key];

            acc[key] = {
               widget:
                  field.type === "string"
                     ? "input"
                     : field.type === "number"
                        ? "number"
                        : "input",
               title: field.title || key,
            };

            return acc;
         }, {}),
      };
   };

   const conFirmSubmit = async (sendRequest: boolean = false) =>
   {
      setLoading?.(true);
      toggleConfirm?.();

      // Transform form data before submission
      const processedFormData = { ...formData };

      Object.keys(processedFormData).forEach((key) =>
      {
         const meta = fieldMeta[key];
         const value = processedFormData[key];
         if (value === undefined || value === null) return;

         // 1. Normalize enum values to match schema enum exactly
         const schemaEnum = meta?.enum;
         if (schemaEnum && Array.isArray(schemaEnum))
         {
            // If the value already matches an enum value exactly, keep it
            if (!schemaEnum.includes(value))
            {
               // Try case-insensitive match
               const matchedEnum = schemaEnum.find(
                  (enumVal: any) =>
                     String(enumVal).toLowerCase() === String(value).toLowerCase(),
               );
               if (matchedEnum !== undefined)
               {
                  processedFormData[key] = matchedEnum;
               }
            }


         }

      });

      const body = resolveTemplate(
         currentStep.submit.body_template,
         processedFormData,
         fieldMeta
      );





      if (currentStep?.pre_validation)
      {
         let data = await fetch(currentStep.pre_validation.url, {
            method: currentStep.pre_validation?.method,
            // body: JSON.stringify(body),
            body: body,
            headers: {
               // "Content-Type": "application/json",
               Accept: "application/json, text/plain, */*",
            },
         });

         if (data.status !== ESttausCode.OK)
         {
            showError(
               "Pre-validation failed. Please check your input and try again.",
            );
            return;
         }
      }

      try
      {
         // const data = await axios({
         //    url: currentStep.submit.url,
         //    method: currentStep.submit?.method || "POST",
         //    data: body,
         //    headers: {
         //       "Content-Type": "application/json",
         //       Accept: "application/json",
         //    },
         // });

         const data: any = await dispatch(addOrderAsync({ ...body, satellites: selectedSatellite })).unwrap();

         if (data?.status !== ESttausCode.OK)
         {


            showError(
               t(data?.response?.data?.message ?? "Create order failed. Please check your input and try again."),
            );
         } else
         {
            const orderId = data?.data?.data?.id;

            if (sendRequest && orderId)
            {
               const statusId = dataStatusOrder?.find(
                  (itemF: any) => itemF?.key === "IN_PROGRESS",
               )?.id;

               try
               {
                  await dispatch(
                     updateStatusOrderAsync({
                        order_id: orderId,
                        status_id: statusId,
                        notes: "",
                     }),
                  ).unwrap();
               } catch (error)
               {
                  // Order was created successfully; surface the send-request failure
                  // separately so the user still lands on the detail page.
                  showError(t("Send order failed. Please send the request again from the order detail page."));
               }
            }

            showSuccess(t("Order submitted successfully!"));
            if (orderId)
               navigate(`/order/management-tasking/${orderId}`); // Navigate to order detail page after successful submission
         }
      } catch (error)
      {


         showError(
            t("Create order failed. Please check your input and try again."),
         );
      } finally
      {
         setLoading?.(false);
      }
   };

   const submit = async () =>
   {
      toggleConfirm?.();
   };

   return {
      workflow,
      currentStep,
      currentStepIndex,
      totalSteps: workflow?.steps?.length + 1 || 0,
      formData,
      setFormData,
      next,
      back,
      submit,
      fieldMeta,
      stepFieldMap,
      setCurrentStepIndex,
      conFirmSubmit,
   };
};
